/**
 * Settings Screen - User preferences
 *
 * Modular screen orchestrating settings sections:
 * - Quota usage display
 * - Subscription and family plan management
 * - Account authentication (sign in/out)
 * - Data management (clear cache, clear history)
 * - App information (version, about, privacy, terms)
 *
 * Business logic extracted to lib/settings/*.
 * Section components are pure presentational.
 */
import { useCallback, useEffect } from "react";
import { View, ScrollView, Platform, Linking } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { useTabScreenPadding } from "../../components/layout/TabScreenWrapper";
import { useRouter } from "expo-router";
import Constants from "expo-constants";
import { differenceInDays } from "date-fns";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { useAuth } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertProvider";
import { captureEvent } from "../../lib/analytics";
import { trackEvent } from "@/lib/analytics/events";
import { useAnalysisHistory as useLocalAnalysisHistory } from "../../features/analysis/hooks/useAnalysisHistory";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useQuota } from "../../hooks/useQuota";
import { useFamilyPlan } from "../../hooks/useFamilyPlan";
import { usePractitionerManage } from "../../hooks/usePractitionerManage";
import { useAnalysisStats } from "@dramadetector/api-client";
import { GlassBackground, GlassPill, GlassCard } from "../../components/glass";
import { SettingsItem } from "../../components/SettingsItem";
import { Logo } from "../../components/Logo";
import { QuotaSection } from "../../components/screens/settings/QuotaSection";
import { SubscriptionSection } from "../../components/screens/settings/SubscriptionSection";
import { AccountSection } from "../../components/screens/settings/AccountSection";
import { LanguageSection } from "../../components/screens/settings/LanguageSection";
import { RetentionSection } from "../../components/screens/settings/RetentionSection";
import { DataManagementSection } from "../../components/screens/settings/DataManagementSection";
import { AppInfoSection } from "../../components/screens/settings/AppInfoSection";
import { PractitionerSettingsSection } from "../../components/screens/settings/PractitionerSettingsSection";
import { AnonCTASection } from "../../components/screens/settings/AnonCTASection";
import { TierGate } from "../../components/access/TierGate";
import { usePractitionerProfile } from "../../hooks/usePractitionerProfile";
import { handleClearCache } from "../../lib/settings/cacheActions";
import { handleClearHistory } from "../../lib/settings/historyActions";
import { useClearHistory } from "../../hooks/useClearHistory";
import { handleAbout, handlePrivacy, handleTerms } from "../../lib/settings/infoDialogs";
import { handleSignOut } from "../../lib/settings/authActions";
import { handleDeleteAllData } from "../../lib/settings/deleteDataActions";
import { useCustomerPortal } from "../../hooks/useCustomerPortal";

export default function SettingsScreen() {
  // ✅ Fix: Disable Suspense for lazy-loaded namespace to prevent race condition (settings namespace not preloaded)
  const { t } = useTranslation('settings', { useSuspense: false }); // Explicit namespace for settings screen
  const { t: tCommon } = useTranslation('common');
  const { t: tMobileApp } = useTranslation('mobile_app'); // Common namespace for shared UI
  const queryClient = useQueryClient();
  const bottomPadding = useTabScreenPadding();
  const router = useRouter();
  const { user, accessToken, loading: authLoading, isAnonymous, signOut } = useAuth();
  const { alert } = useAlert();

  // Local history (Zustand store)
  const { clearHistory: clearLocalHistory } = useLocalAnalysisHistory();

  // Get actual count from stats (not paginated)
  const { data: stats } = useAnalysisStats({
    userId: user?.id,
    enabled: !!user && !authLoading && !!accessToken,
  });

  // Quota usage data (NEW: useEntitlements + useQuota architecture)
  // useEntitlements: static entitlements (tier, characterLimit, quotaPolicy) - 5min cache
  // useQuota: dynamic usage (used, remaining, resetAt) - 30sec cache + defensive guard
  const { tier, quotaPolicy, refetch: refetchEntitlements } = useEntitlements();
  const {
    used: dailyAnalysesUsed,
    remaining,
    resetAt,
    usagePercent,
    formattedResetTime,
    isLoading: isLoadingQuota,
    error: quotaError,
    refetch: refetchQuota,
  } = useQuota(quotaPolicy);

  // Family plan data
  const { isFamilyMember, isFamilyAdmin, familyPlan } = useFamilyPlan();

  // Practitioner groups data (Phase 6 - multi-group support)
  const { totalGroups: practitionerGroupCount } = usePractitionerManage();

  // Practitioner profile data (role switching UX — task #1809)
  const { isPractitioner, profile: practitionerProfile } = usePractitionerProfile();

  const refetchQuotaAndEntitlements = useCallback(async () => {
    refetchEntitlements();
    refetchQuota();
  }, [refetchEntitlements, refetchQuota]);

  // Calculate user tenure (days since signup)
  const userTenureDays = user?.createdAt
    ? differenceInDays(new Date(), new Date(user.createdAt))
    : 0;

  // Track screen view (NEW - Phase 1.5)
  useEffect(() => {
    captureEvent('feature_used', {
      feature: 'settings',
      screen: 'settings',
      user_tenure_days: userTenureDays,
    });

    // y2k_design_view retired (#797 — redundant design engagement event)
  }, [userTenureDays]);

  // Anon-engagement funnel: pairs with Anon_Settings_CtaClicked.
  useEffect(() => {
    if (isAnonymous && user?.id) {
      captureEvent('Anon_Settings_Viewed', { source: 'settings' });
    }
  }, [isAnonymous, user?.id]);

  const onManageSubscription = useCallback(() => {
    router.push("/(tabs)/subscription");
  }, [router]);

  const handleClearCacheAction = useCallback(() => {
    handleClearCache({ userId: user?.id, alert, t, tCommon, queryClient });
  }, [user?.id, alert, t, tCommon, queryClient]);

  const clearHistoryMutation = useClearHistory();
  const handleClearHistoryAction = useCallback(() => {
    handleClearHistory({
      user,
      stats,
      alert,
      t,
      tCommon,
      queryClient,
      clearLocalHistory,
      clearHistory: clearHistoryMutation.mutateAsync,
    });
  }, [user, stats, alert, t, tCommon, queryClient, clearLocalHistory, clearHistoryMutation.mutateAsync]);

  const handleAboutAction = useCallback(() => {
    handleAbout({ alert, t, tCommon });
  }, [alert, t, tCommon]);

  const handlePrivacyAction = useCallback(() => {
    handlePrivacy({ alert, t, tCommon });
  }, [alert, t, tCommon]);

  const handleTermsAction = useCallback(() => {
    handleTerms({ alert, t, tCommon });
  }, [alert, t, tCommon]);

  const handleSignOutAction = useCallback(async () => {
    await handleSignOut({ alert, t, tCommon, signOut, router });
  }, [alert, t, tCommon, signOut, router]);

  const handleDeleteAllDataAction = useCallback(() => {
    handleDeleteAllData({ user, alert, t, tCommon, queryClient, clearLocalHistory });
  }, [user, alert, t, tCommon, queryClient, clearLocalHistory]);

  const { openPortal, isOpening: isOpeningPortal } = useCustomerPortal();
  const handleOpenBillingPortal = async () => {
    if (isOpeningPortal) return;
    try {
      const data = await openPortal();
      if (!data?.portal_url) {
        alert(
          t('billing.error_title', { defaultValue: 'Error' }),
          t('billing.error_message', { defaultValue: 'Could not open billing portal. Please try again.' }),
        );
        return;
      }
      await Linking.openURL(data.portal_url);
    } catch {
      alert(
        t('billing.error_title', { defaultValue: 'Error' }),
        t('billing.error_message', { defaultValue: 'Could not open billing portal. Please try again.' }),
      );
    }
  };

  /**
   * Navigate to the Practitioner Dashboard from the Professional section.
   * Tracks analytics event and pushes the (practitioner)/dashboard route.
   */
  const handleOpenDashboard = useCallback(() => {
    trackEvent('Button_Click_OpenPractitionerDashboard', {});
    router.push('/(practitioner)/dashboard' as never);
  }, [router]);

  /**
   * Mobile Demand Signal: Track user interest in native iOS/Android app
   *
   * This helps validate Phase 2 mobile app development priority.
   * PostHog Dashboard: Mobile Demand Signals (633090)
   */
  const handleRequestMobileApp = () => {
    // Determine user segment based on tier
    const userSegment: 'free' | 'trial' | 'paid' = (() => {
      if (!user) return 'trial';
      if (tier === 'drama_plus' || tier === 'dramax') return 'paid';
      return 'free';
    })();

    // Track the demand signal
    trackEvent('Mobile_AppRequested', {
      source: 'settings',
      platform: Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'both',
      user_segment: userSegment,
    });

    // Show confirmation dialog
    // Note: alert() expects separate arguments (title, message, buttons), not an object
    alert(
      tMobileApp('confirm_title'),
      tMobileApp('confirm_message'),
      [
        {
          text: tCommon('cancel'),
          style: 'cancel',
        },
        {
          text: tMobileApp('confirm_button'),
          onPress: () => {
            // Track positive response (user wants notification)
            trackEvent('Mobile_AppRequested_Confirmed', {
              platform: Platform.OS as 'ios' | 'android' | 'web',
              user_segment: userSegment,
            });
            alert('✅', tCommon('success'));
          },
        },
      ]
    );
  };

  return (
    <GlassBackground>
      <View className="flex-1">
        {/* Header */}
        <View className="mb-6 items-center px-4 md:px-8 lg:px-12 pt-4">
          <Logo size="lg" style={{ marginBottom: 16 }} />
          <GlassPill translationKey="tabs.settings" size="lg" />
          <View className="mt-2">
            <TierBody size="lg" color="text-gray-600">
              {t('subtitle')}
            </TierBody>
          </View>
        </View>

      {/* Content */}
      <ScrollView contentContainerStyle={{ paddingBottom: bottomPadding }} contentContainerClassName="p-4 md:p-8 lg:p-12">
        <View className="w-full max-w-2xl mx-auto">
          {/* Usage & Quota Section */}
          {/* Always render - isLoading state handles initial load (prevents 10/10 flash) */}
          <QuotaSection
            dailyAnalysesUsed={dailyAnalysesUsed}
            dailyAnalysesLimit={quotaPolicy.limit}
            usagePercent={usagePercent}
            remaining={remaining}
            resetTime={formattedResetTime}
            resetAt={resetAt}
            currentTier={tier}
            onUpgradeClick={() => router.push("/(tabs)/subscription")}
            onRefresh={refetchQuotaAndEntitlements}
            isLoading={isLoadingQuota}
            error={quotaError?.message ?? null}
          />

          {/* Subscription Section — manifest-gated (NocoDB #4080 / #4085 C3B) */}
          <TierGate feature="feature:subscription_management">
            <SubscriptionSection
              onManageSubscription={onManageSubscription}
              onManageFamily={isFamilyMember ? () => router.push("/(tabs)/family-manage") : undefined}
              isFamilyMember={isFamilyMember}
              isFamilyAdmin={isFamilyAdmin}
              familyTier={familyPlan?.tier}
              onManagePractitionerGroups={
                practitionerGroupCount > 0
                  ? () => router.push("/(tabs)/practitioner-manage")
                  : undefined
              }
              practitionerGroupCount={practitionerGroupCount}
              onOpenBillingPortal={handleOpenBillingPortal}
            />
          </TierGate>

          {/* Professional Section — Practitioner role switching (task #1809) */}
          {isPractitioner && practitionerProfile && (
            <PractitionerSettingsSection
              profile={practitionerProfile}
              onOpenDashboard={handleOpenDashboard}
            />
          )}

          {/* Practitioner Access Section */}
          <View className="mt-4">
            <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
              {t('sections.privacy', { defaultValue: 'Privacy' })}
            </TierBody>
            <GlassCard>
              <SettingsItem
                icon="🩺"
                label={t('practitioner_access.title')}
                description={t('practitioner_access.description')}
                onPress={() => router.push('/practitioner-access' as never)}
                testID="practitioner-access-settings-item"
              />
            </GlassCard>
          </View>

          {/* Anon CTA Section — retention-framed upsell for anon users (source=anon_settings) */}
          {isAnonymous && <AnonCTASection />}

          {/* Account Section — manifest-gated (NocoDB #4080 / #4085 C3B) */}
          <TierGate feature="feature:account_settings">
            <AccountSection
              user={user}
              onSignOut={handleSignOutAction}
            />
          </TierGate>

          {/* Language Section */}
          <LanguageSection />

          {/* Data Retention Section (M4.3 - GDPR Art. 5(1)(e)) — manifest-gated (NocoDB #4080 / #4085 C3B) */}
          <TierGate feature="feature:retention_preference">
            <RetentionSection />
          </TierGate>

          {/* Data Management Section */}
          <DataManagementSection
            onClearCache={handleClearCacheAction}
            onClearHistory={handleClearHistoryAction}
            onDeleteAllData={handleDeleteAllDataAction}
            isAuthenticated={!!user}
          />

          {/* App Information Section */}
          <AppInfoSection
            appVersion={Constants.expoConfig?.version || "0.1.0"}
            onAbout={handleAboutAction}
            onPrivacy={handlePrivacyAction}
            onTerms={handleTermsAction}
            onRequestMobileApp={handleRequestMobileApp}
          />

          {/* Footer */}
          <View className="p-6">
            <TierBody size="xs" className="text-gray-500 text-center">
              {t('footer.version', { version: Constants.expoConfig?.version || "0.1.0" })}
            </TierBody>
            <TierBody size="xs" className="text-gray-400 text-center mt-1">
              {t('footer.powered_by')}
            </TierBody>
            <TierBody size="xs" className="text-gray-400 text-center mt-1">
              {t('footer.tagline')}
            </TierBody>
          </View>
        </View>
      </ScrollView>
      </View>
    </GlassBackground>
  );
}
